import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import CustomerLayout from '../components/CustomerLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  MapPin, Calendar, Users, Star, Search, Sparkles, Phone, ArrowLeft, Home
} from 'lucide-react';

// Traditional decorative elements
const TempleSpire = () => (
  <svg viewBox="0 0 40 60" className="w-full h-full" fill="currentColor">
    <path d="M20 0 L25 15 L22 15 L22 20 L25 20 L25 25 L22 25 L22 30 L28 30 L28 55 L30 55 L30 60 L10 60 L10 55 L12 55 L12 30 L18 30 L18 25 L15 25 L15 20 L18 20 L18 15 L15 15 Z" />
  </svg>
);

const FloralCorner = () => (
  <svg viewBox="0 0 50 50" className="w-full h-full" fill="currentColor">
    <circle cx="10" cy="10" r="3" />
    <path d="M10 5 Q15 10 10 15 Q5 10 10 5" />
    <path d="M5 10 Q10 5 15 10 Q10 15 5 10" />
    <circle cx="25" cy="5" r="2" />
    <circle cx="5" cy="25" r="2" />
    <path d="M15 15 Q25 5 35 15" fill="none" stroke="currentColor" strokeWidth="1" />
  </svg>
);

export default function NammaYatraPage() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All Tours');
  const [searchQuery, setSearchQuery] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const categories = [
    { name: 'All Tours', icon: '🗺️' },
    { name: 'Heritage', icon: '🏛️' },
    { name: 'Nature', icon: '🌄' },
    { name: 'Spiritual', icon: '🕉️' },
    { name: 'Adventure', icon: '🏔️' }
  ];

  useEffect(() => {
    fetchPackages();
  }, [selectedCategory]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/packages`, {
        params: selectedCategory !== 'All Tours' ? { category: selectedCategory } : {}
      });
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPackages = packages.filter(pkg =>
    pkg.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    pkg.destination?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleViewDetails = (pkg) => {
    const pkgId = pkg._id || pkg.id;
    navigate(`/package/${pkgId}`);
  };

  const getMinPrice = (pkg) => {
    if (pkg.duration_variants && pkg.duration_variants.length > 0) {
      return Math.min(...pkg.duration_variants.map(v => v.price || 0));
    }
    return pkg.price || 0;
  };

  return (
    <CustomerLayout>
      <div className="min-h-screen bg-[#FAFDF8]">
        {/* Hero Section with Traditional Design */}
        <div className="bg-gradient-to-r from-[#8B4513] via-[#D2691E] to-[#8B4513] text-white relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-4 left-4 w-12 h-12 text-white/10">
              <FloralCorner />
            </div>
            <div className="absolute top-4 right-4 w-12 h-12 text-white/10 rotate-90">
              <FloralCorner />
            </div>
            <div className="absolute bottom-0 left-20 w-8 h-16 text-white/10">
              <TempleSpire />
            </div>
            <div className="absolute bottom-0 right-20 w-8 h-16 text-white/10">
              <TempleSpire />
            </div>
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-20 text-white/10">
              <TempleSpire />
            </div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 py-16">
            <div className="text-center">
              <div className="inline-flex items-center bg-[#FFD700] text-[#8B4513] px-4 py-2 rounded-full mb-6">
                <Sparkles className="w-4 h-4 mr-2" />
                <span className="font-bold text-sm">NammaYatra Travel</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Discover Incredible India
              </h1>
              <p className="text-lg text-orange-100 max-w-2xl mx-auto mb-8">
                Experience the magic of India through our carefully curated travel packages.
              </p>

              {/* Search Bar */}
              <div className="max-w-2xl mx-auto bg-white rounded-xl p-3 shadow-2xl">
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8B4513] w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search destinations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border-2 border-[#D2691E]/20 rounded-lg focus:border-[#D2691E] focus:outline-none text-gray-900"
                    />
                  </div>
                  <Button className="bg-[#D2691E] hover:bg-[#8B4513] text-white font-bold px-6 rounded-lg">
                    <Search className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="max-w-7xl mx-auto px-4 -mt-6 relative z-10">
          <Card className="border-2 border-[#D2691E]/20 shadow-xl bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-center gap-2 overflow-x-auto">
                {categories.map((category) => (
                  <button
                    key={category.name}
                    onClick={() => setSelectedCategory(category.name)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold whitespace-nowrap transition-all ${
                      selectedCategory === category.name
                        ? 'bg-[#D2691E] text-white shadow-lg scale-105'
                        : 'bg-white text-[#8B4513] hover:bg-[#D2691E]/10 border-2 border-[#D2691E]/20'
                    }`}
                  >
                    <span>{category.icon}</span>
                    <span className="text-sm">{category.name}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Packages Grid */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border-4 border-[#D2691E]/20 border-t-[#D2691E] rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-[#8B4513]/70">Loading packages...</p>
            </div>
          ) : filteredPackages.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-[#8B4513]">
                    {selectedCategory === 'All Tours' ? 'All Packages' : `${selectedCategory} Tours`}
                  </h2>
                  <p className="text-[#8B4513]/70">{filteredPackages.length} packages available</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPackages.map((pkg) => (
                  <Card 
                    key={pkg.id || pkg._id} 
                    className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 border-[#D2691E]/20 hover:border-[#D2691E]/50 bg-white"
                  >
                    {/* Package Image */}
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={pkg.image_url || 'https://images.unsplash.com/photo-1609137144813-7d9921338f24?w=500'}
                        alt={pkg.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                      
                      <Badge className="absolute top-3 left-3 bg-[#FFD700] text-[#8B4513] font-bold">
                        {pkg.category}
                      </Badge>

                      <div className="absolute top-3 right-3 bg-white/90 px-2 py-1 rounded-full flex items-center gap-1">
                        <Star className="w-4 h-4 text-[#FFD700] fill-current" />
                        <span className="font-semibold text-sm text-[#8B4513]">{pkg.rating || 4.5}</span>
                      </div>
                    </div>

                    <CardContent className="p-4 space-y-3">
                      <h3 className="text-lg font-bold text-[#8B4513] line-clamp-2">
                        {pkg.name}
                      </h3>
                      <p className="text-[#8B4513]/70 text-sm line-clamp-2">{pkg.description}</p>

                      <div className="space-y-1.5">
                        <div className="flex items-center text-sm text-[#8B4513]/70">
                          <MapPin className="w-4 h-4 mr-2 text-[#D2691E]" />
                          <span>{pkg.destination}</span>
                        </div>
                        <div className="flex items-center text-sm text-[#8B4513]/70">
                          <Calendar className="w-4 h-4 mr-2 text-[#D2691E]" />
                          <span>{pkg.duration_variants?.length || 1} duration options</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t border-[#D2691E]/20">
                        <div>
                          <p className="text-xs text-[#8B4513]/70">Starting from</p>
                          <p className="text-xl font-bold text-[#D2691E]">₹{getMinPrice(pkg).toLocaleString()}</p>
                        </div>
                        <Button 
                          className="bg-[#D2691E] hover:bg-[#8B4513] text-white"
                          onClick={() => handleViewDetails(pkg)}
                        >
                          View Details
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <MapPin className="w-16 h-16 text-[#D2691E]/30 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#8B4513] mb-2">No packages found</h3>
              <p className="text-[#8B4513]/70 mb-6">
                {searchQuery ? `No packages match "${searchQuery}"` : `No packages available in ${selectedCategory}`}
              </p>
              {searchQuery && (
                <Button onClick={() => setSearchQuery('')} variant="outline" className="border-[#D2691E] text-[#D2691E]">
                  Clear Search
                </Button>
              )}
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-[#8B4513] to-[#D2691E] text-white py-12 relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute bottom-0 left-10 w-8 h-16 text-white/10">
              <TempleSpire />
            </div>
            <div className="absolute bottom-0 right-10 w-8 h-16 text-white/10">
              <TempleSpire />
            </div>
          </div>
          <div className="max-w-4xl mx-auto px-4 text-center relative">
            <h2 className="text-2xl font-bold mb-4">Need Help Planning Your Trip?</h2>
            <p className="text-orange-100 mb-6">Our travel experts are here to help</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="tel:+918001234567" className="flex items-center gap-2 bg-[#FFD700] text-[#8B4513] px-6 py-3 rounded-lg font-bold">
                <Phone className="w-5 h-5" />
                <span>Call Us</span>
              </a>
              <Link to="/contact" className="border-2 border-white text-white hover:bg-white hover:text-[#8B4513] px-6 py-3 rounded-lg font-bold transition-colors">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
}
